import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { asPerformanceDateLong } from 'utils/date-formatter';
import { DatePicker } from 'components/UI/DatePicker/DatePicker';
import { ReactComponent as IconTicket3 } from 'assets/icons/svgs/icon-ticket3.svg';
import { ReactComponent as IconFestival1 } from 'assets/icons/svgs/icon-festival1.svg';
import { ReactComponent as IconPackage } from 'assets/icons/svgs/icon-package.svg';
import { ReactComponent as IconTicket2 } from 'assets/icons/svgs/icon-ticket2.svg';
import { EventFiltersContext } from 'components/Shared/EventFiltersProvider';
import { SelectTimeButton } from './SelectTimeButton/SelectTimeButton';
import { filterPerformanceTimesBySelectedDate } from '../booking-modal-utils';

import './BookingModalSelectTime.scss';

export const BookingModalSelectTime = (props) => {
  const {
    dateSelected,
    CalendarData,
    PerformanceDateTimes,
    performanceIdSelected,
    performanceIdSelectedHandler,
    calendarDateClickHandler,
    HasExternalLinks,
    SeasonPass,
    Festival,
    Package,
    Subscription,
  } = props;
  const { startDate } = useContext(EventFiltersContext);

  /*
   * For the selected date, find all the performances that match that date
   */
  const performancesForSelectedDate = filterPerformanceTimesBySelectedDate(
    dateSelected,
    PerformanceDateTimes
  );

  /*
   * Select the first performance
   * IF no performance is already selected
   * AND if there is at least one performance
   * AND if the first performance is available
   */
  if (
    !performanceIdSelected &&
    performancesForSelectedDate?.length === 1 &&
    performancesForSelectedDate[0].Available
  ) {
    performanceIdSelectedHandler(performancesForSelectedDate[0].PerformanceId);
  }

  const oneDate = (
    <>
      <h2 className="select-section__heading">Event date:</h2>
      <p className="select-time__date">
        {asPerformanceDateLong(PerformanceDateTimes[0].PerformanceDateTime)}
      </p>
      <ul className="select-time__list">
        <li key={PerformanceDateTimes[0].PerformanceId}>
          <SelectTimeButton
            {...PerformanceDateTimes[0]}
            performanceIdSelectedHandler={performanceIdSelectedHandler}
            isSelected
          />
        </li>
      </ul>
    </>
  );

  const multipleDates = (
    <>
      <h2 className="select-section__heading">Select a time:</h2>
      <ul className="select-time__list">
        {performancesForSelectedDate?.map((timeItem) => {
          const isSelected = performanceIdSelected === timeItem.PerformanceId;
          return (
            <li key={timeItem.PerformanceId}>
              <SelectTimeButton
                {...timeItem}
                isSelected={isSelected}
                performanceIdSelectedHandler={performanceIdSelectedHandler}
              />
            </li>
          );
        })}
      </ul>
    </>
  );

  const externalLinksContent = (
    <div className="col-sm-5 pl-sm-0 center-self">
      {SeasonPass && (
        <a
          href={SeasonPass.key}
          target={SeasonPass.name}
          className="text-bar other-buy-option link-chevr-lg"
        >
          <strong>
            <IconTicket3 className="icon" />
            Buy as part of a Season Pass
          </strong>
        </a>
      )}
      {Festival && (
        <a
          href={Festival.key}
          target={Festival.name}
          className="text-bar other-buy-option link-chevr-lg"
        >
          <strong>
            <svg className="icon">
              <IconFestival1 className="icon" />
            </svg>
            Buy as part of a festival
          </strong>
        </a>
      )}
      {Package && (
        <a
          href={Package.key}
          target={Package.name}
          className="text-bar other-buy-option link-chevr-lg"
        >
          <strong>
            <svg className="icon">
              <IconPackage className="icon" />
            </svg>
            Buy as part of a package
          </strong>
        </a>
      )}
      {Subscription && (
        <a
          href={Subscription.key}
          target={Subscription.name}
          className="text-bar other-buy-option link-chevr-lg"
        >
          <strong>
            <svg className="icon">
              <IconTicket2 className="icon" />
            </svg>
            Buy as part of a subscription
          </strong>
        </a>
      )}
    </div>
  );

  const firstDateIsSelected = useMemo(() => dateSelected || startDate, []);
  const isMultiplePerformanceDates = PerformanceDateTimes.length > 1;
  const performanceDatesButtonsVariant = isMultiplePerformanceDates ? multipleDates : oneDate;
  const performanceDatesContent = PerformanceDateTimes?.length && performanceDatesButtonsVariant;

  return (
    <div className="booking-modal__select-time" data-testid="modal-select-time">
      {isMultiplePerformanceDates && (
        <>
          <h2 className="select-section__heading">Select a date:</h2>
          <div className="select-time__datepicker" data-testid="modal-datepicker">
            <DatePicker
              CalendarData={CalendarData}
              dateClickHandler={calendarDateClickHandler}
              firstDateIsSelected={firstDateIsSelected}
              componentClass="booking-modal"
            />
          </div>
        </>
      )}
      {performanceDatesContent}
      {HasExternalLinks && (
        <div className="col-sm-2">
          <div className="divider-line">
            <span>or</span>
          </div>
        </div>
      )}
      {HasExternalLinks && externalLinksContent}
      <p>
        <small>Online orders may be subject to a Service Charge from $8.95 per order.</small>
      </p>
    </div>
  );
};

BookingModalSelectTime.propTypes = {
  dateSelected: PropTypes.instanceOf(Date),
  CalendarData: PropTypes.object,
  PerformanceDateTimes: PropTypes.array,
  HasExternalLinks: PropTypes.bool,
  SeasonPass: PropTypes.object,
  Festival: PropTypes.object,
  Package: PropTypes.object,
  Subscription: PropTypes.object,
  performanceIdSelected: PropTypes.number,
  performanceIdSelectedHandler: PropTypes.func,
  calendarDateClickHandler: PropTypes.func,
};
